import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Soup, Check, Plus, Minus } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

export function useLeftovers(activeId) {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    if (!activeId) return;
    try { const { data } = await api.get(`/leftovers/${activeId}`); setItems(data); } catch { /* optional */ }
  }, [activeId]);
  useEffect(() => { load(); }, [load]);
  const eat = async (id) => {
    try { await api.post(`/leftovers/${id}/eaten`); setItems((p) => p.filter((l) => l.id !== id)); toast.success("Yum — leftovers sorted 🙌"); }
    catch { toast.error("Couldn't update that"); }
  };
  return { items, reload: load, eat };
}

export function LeftoversCard({ activeId }) {
  const { items, eat } = useLeftovers(activeId);
  if (!items.length) return null;
  const portions = items.reduce((s, l) => s + (l.portions || 0), 0);
  return (
    <div className="bg-[#E7E0F3] rounded-3xl border-2 border-white/60 p-4 shadow-soft" data-testid="leftovers-card">
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-2xl bg-white/70 flex items-center justify-center text-purple-500"><Soup size={18} strokeWidth={2.5} /></span>
        <div className="flex-1">
          <p className="font-heading font-semibold text-sorted-ink leading-tight">Leftovers in the fridge</p>
          <p className="text-[11px] font-semibold text-sorted-slate">{portions} portion{portions === 1 ? "" : "s"} — tomorrow's lunch is sorted, no need to shop for it</p>
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        {items.map((l) => (
          <div key={l.id} className="bg-white/80 rounded-2xl px-3 py-2 flex items-center gap-2" data-testid={`leftover-${l.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-sorted-ink truncate">{l.meal_title} <span className="text-sorted-slate font-semibold">× {l.portions}</span></p>
              <p className="text-[11px] text-sorted-slate">{l.days_left <= 0 ? "Eat today" : `Use within ${l.days_left} day${l.days_left === 1 ? "" : "s"}`}{l.note ? ` · ${l.note}` : ""}</p>
            </div>
            <button onClick={() => eat(l.id)} data-testid={`leftover-eaten-${l.id}`} className="bg-sorted-rose text-white text-[11px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1"><Check size={12} strokeWidth={3} /> Eaten</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function LogLeftovers({ activeId, mealTitle, day }) {
  const [portions, setPortions] = useState(2);
  const [saved, setSaved] = useState(false);
  const save = async () => {
    try { await api.post("/leftovers", { household_id: activeId, meal_title: mealTitle, portions, day }); setSaved(true); toast.success(`${portions} portion${portions === 1 ? "" : "s"} saved — I'll plan lunch around them`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  if (saved) return <p className="mt-3 text-sm font-bold text-sorted-ink flex items-center gap-1" data-testid="leftovers-saved"><Check size={15} strokeWidth={3} className="text-emerald-600" /> Leftovers logged — tomorrow's lunch, sorted.</p>;
  return (
    <div className="mt-3 bg-white rounded-3xl border-2 border-[#FDECEF] p-3 shadow-soft" data-testid="log-leftovers">
      <p className="font-heading font-semibold text-sorted-ink text-sm">Any leftovers?</p>
      <p className="text-xs text-sorted-slate">Pop them in the fridge and I'll skip shopping for tomorrow's lunch.</p>
      <div className="flex items-center gap-3 mt-3">
        <button onClick={() => setPortions(Math.max(1, portions - 1))} data-testid="leftovers-minus" aria-label="Fewer" className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-sorted-ink"><Minus size={15} strokeWidth={3} /></button>
        <span className="font-heading font-bold text-xl w-20 text-center text-sorted-ink" data-testid="leftovers-count">{portions} {portions === 1 ? "portion" : "portions"}</span>
        <button onClick={() => setPortions(portions + 1)} data-testid="leftovers-plus" aria-label="More" className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-sorted-ink"><Plus size={15} strokeWidth={3} /></button>
        <button onClick={save} data-testid="leftovers-save" className="ml-auto bg-sorted-rose text-white text-xs font-bold px-4 py-2.5 rounded-full">Save</button>
      </div>
    </div>
  );
}
